import { resolveDefaultPostAuthLandingPath, resolveBootCustomerFeatureFlags, resolvePlatformHubPath } from '../../features/shop/resolveShopHref.js';
import { isCustomerBrowseEnabled } from '../../config/customerFeatureFlags.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import { withDirectoryMapViewQuery } from './directoryViewMode.js';
import { isMapResumeReturnPath } from './isMapResumeReturnPath.js';
import { isPaymentResumeReturnPath } from './isPaymentResumeReturnPath.js';
import { normalizeSalesPointSlug } from './lastKnownSalesPoint.js';
/* Tenant-scoped return-target storage + validation.
 *
 * Plan §"Routing & Return-To-Origin Strategy":
 *   - Storage key: `sessionStorage["customer:returnTo:{tenantCode}"]`.
 *   - Payload: `{ pathWithQuery, createdAtMs, source? }`.
 *   - Allowed targets: tenant-local path `/${tenantCode}/...` (with optional
 *     query) — NEVER absolute URLs, protocol-relative, sign-in loops, etc.
 *   - Validation is performed AFTER URL decode + path normalization so encoded
 *     bypass patterns (`%2f%2f`, `%2e%2e`, mixed-encoding, repeated decode) are
 *     rejected.
 *   - TTL: 30 minutes from `createdAtMs`.
 *   - Semantics: consume-once after successful auth (read + clear).
 *
 * This module is dependency-free so it can be reused by validator tests and
 * by routing/guards without dragging React Router internals in.
 */

export const RETURN_TARGET_TTL_MS = 30 * 60 * 1000;

const STORAGE_PREFIX = 'customer:returnTo:';
const MAX_DECODE_PASSES = 3;
/** Mirrors OIDC_LAND_QUERY_PARAM in oidcCookieLand.ts — keep local to stay dependency-free. */
const OIDC_LAND_QUERY_PARAM = 'oidcLand';

/**
 * H1 LOCK — strip `oidcLand` (and empty query) from a path-with-query so the
 * land signal is never persisted or re-navigated after force-strip / auth.
 * Shared by `saveReturnTarget`, `setInMemoryReturnTarget`, and recovery resume/retry.
 */
export function stripOidcLandQueryParam(pathWithQuery: string): string {
  const qIndex = pathWithQuery.indexOf('?');
  if (qIndex === -1) {
    return pathWithQuery;
  }
  const path = pathWithQuery.slice(0, qIndex);
  const query = pathWithQuery.slice(qIndex + 1);
  if (query.length === 0) {
    return path;
  }
  const params = new URLSearchParams(query);
  if (!params.has(OIDC_LAND_QUERY_PARAM)) {
    return pathWithQuery;
  }
  params.delete(OIDC_LAND_QUERY_PARAM);
  const nextQuery = params.toString();
  return nextQuery.length > 0 ? `${path}?${nextQuery}` : path;
}

/**
 * Strip `oidcLand` from a React Router `location.search` ("" or "?…") before
 * forwarding it onto a new navigate/`<Navigate>`. After force-strip,
 * `history.replaceState` clears window but RR search can still hold oidcLand —
 * copying RR search without this re-seeds land on window.
 */
export function stripOidcLandFromSearch(search: string): string {
  if (search.length === 0 || search === '?') {
    return '';
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  if (!params.has(OIDC_LAND_QUERY_PARAM)) {
    return search.startsWith('?') ? search : `?${query}`;
  }
  params.delete(OIDC_LAND_QUERY_PARAM);
  const nextQuery = params.toString();
  return nextQuery.length > 0 ? `?${nextQuery}` : '';
}

function hasAsciiControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

export type ReturnTargetSource = 'shop' | 'checkout' | 'donate' | 'account';

export interface ReturnTargetPayload {
  pathWithQuery: string;
  createdAtMs: number;
  source?: ReturnTargetSource;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Optional — used to scan orphan `customer:returnTo:{tenant}` twins. */
  readonly length?: number;
  key?(index: number): string | null;
}

interface ValidateOptions {
  tenantCode: string;
  /** Test seam — defaults to `Date.now()`. */
  nowMs?: number;
}

/** Tenant-code character whitelist mirrors backend path matcher (N13 export). */
export const TENANT_CODE_REGEX = /^[a-z0-9][a-z0-9-]{0,63}$/i;

/** Platform (cross-tenant) return key — N11. */
export const PLATFORM_RETURN_STORAGE_KEY = 'customer:returnTo:platform';

/**
 * Guest clicked Moje objednávky on platform chrome (`/` / `/shops`) before a
 * home membership was known — resume at platform `/account/orders` after auth.
 */
export const POST_AUTH_ORDERS_INTENT_KEY = 'customer:postAuthIntent:orders';

/**
 * G1 — after platform OIDC lands on an allowlisted returnTo (often `/account`),
 * FE consumes the path-tenant park using this hint (bare `/{tenant}/shop` is not
 * a valid BE `validatePlatformOidcReturnTo` target).
 */
export const OIDC_POST_AUTH_TENANT_HINT_KEY = 'customer:oidcPostAuthTenant';

/**
 * G1 — durable checkout/donate resume park written by
 * {@link parkPaymentResumeReturnTarget}. Survives in-memory wipe (OIDC full
 * document) and SoftRace handoff microtask clear so RequireGuestAuth cannot
 * bounce to `/browse` after twin/memory were already consumed.
 */
export const PAYMENT_RESUME_RETURN_KEY = 'customer:paymentResumeReturn';

/**
 * G1 — durable map/directory resume park written by
 * {@link parkMapResumeReturnTarget}. Peeks after payment-resume and before
 * leftover orders/platform so abandoned checkout cannot steal a Views CTA,
 * and chrome re-parks cannot steal after a successful map elevate.
 */
export const MAP_RESUME_RETURN_KEY = 'customer:mapResumeReturn';

interface PaymentResumeReturnPayload {
  pathWithQuery: string;
  tenantCode: string;
  createdAtMs: number;
}

interface MapResumeReturnPayload {
  pathWithQuery: string;
  tenantCode: string;
  createdAtMs: number;
}

/**
 * Wave C/D platform hub prefixes — parity with BE `HUB_PREFIXES` in
 * `validatePlatformOidcReturnTo` (includes Stage 3 `/browse` + `/scan`).
 */
const PLATFORM_HUB_PREFIXES = [
  '/browse',
  '/shops',
  '/favorites',
  '/scan',
  '/orgs',
  '/account',
  '/more',
  '/orders/find',
  '/help',
] as const;

/** Same-turn post-auth redirect (highest N11 priority). */
let inMemoryReturnTarget: string | null = null;

/**
 * G2 SoftRace — after consume-once navigate, guest-auth peek must still see the
 * destination so RequireGuestAuth / useGuestAuthScreenDefense do not bounce to
 * Stage 3 empty-guest default `/browse` (Nakupovat).
 *
 * Lifetime (G1):
 * - Peek does **not** clear (Strict Mode double-render safe).
 * - `navigateToPostAuthReturnTarget` publishes then schedules a microtask clear
 *   so same-tick SoftRace peek can still win once; after the microtask, a later
 *   accidental `/sign-in` cannot steal a stale handoff (OIDC restore / navigate-
 *   first SoftRace never mount RequireGuestAuth clear).
 * - Eager clear still runs on signed-in guest-auth Navigate (G3) / leave /
 *   defense-hook bounce (bumps generation so a pending microtask is a no-op).
 */
let postAuthNavigateHandoff: string | null = null;

/** Invalidates pending {@link scheduleClearPostAuthNavigateHandoffAfterSoftRacePeek}. */
let postAuthNavigateHandoffClearGeneration = 0;

/**
 * Decode a target path repeatedly (bounded) so mixed-encoding bypasses (e.g.
 * `%252e%252e/%2fsign-in`) are flattened before structural checks run.
 * Returns `null` if any pass throws (malformed URI).
 */
function safeRepeatDecode(input: string): string | null {
  let current = input;
  for (let i = 0; i < MAX_DECODE_PASSES; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (next === current) {
      return next;
    }
    current = next;
  }
  return current;
}

/**
 * Normalize a path: collapse consecutive slashes, strip `.` segments, reject
 * `..` traversal. We deliberately operate on the path part only — the query
 * string is preserved verbatim (it is never used as a routing decision).
 */
function normalizePath(path: string): string | null {
  if (!path.startsWith('/')) {
    return null;
  }
  const segments = path.split('/');
  const out: string[] = [];
  for (const raw of segments) {
    if (raw === '' || raw === '.') {
      continue;
    }
    if (raw === '..') {
      return null;
    }
    out.push(raw);
  }
  return `/${out.join('/')}`;
}

/**
 * Validate a candidate return target for a given tenant. Returns the
 * normalized (decoded, slash-collapsed) path-with-query string if valid;
 * `null` otherwise. The normalized string is what callers should navigate to.
 */
export function validateReturnTarget(
  rawPathWithQuery: string,
  tenantCode: string
): string | null {
  if (typeof rawPathWithQuery !== 'string' || rawPathWithQuery.length === 0) {
    return null;
  }
  if (!TENANT_CODE_REGEX.test(tenantCode)) {
    return null;
  }

  // Reject protocol-relative and absolute URLs up-front. Both `//evil.com/x`
  // and `https://evil.com/x` would otherwise survive path normalization in
  // some routers.
  if (rawPathWithQuery.startsWith('//') || rawPathWithQuery.startsWith('\\')) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(rawPathWithQuery)) {
    return null;
  }

  const [rawPath, ...rawQueryParts] = rawPathWithQuery.split('?');
  const rawQuery = rawQueryParts.join('?');

  const decodedPath = safeRepeatDecode(rawPath ?? '');
  if (decodedPath === null) {
    return null;
  }

  // After decode, re-check protocol-relative / scheme prefixes that may have
  // been smuggled through URL-encoding (e.g. `%2f%2fevil.com`).
  if (decodedPath.startsWith('//') || decodedPath.startsWith('\\')) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(decodedPath)) {
    return null;
  }

  const normalizedPath = normalizePath(decodedPath);
  if (normalizedPath === null) {
    return null;
  }

  const tenantPrefix = `/${tenantCode}`;
  if (
    normalizedPath !== tenantPrefix &&
    !normalizedPath.startsWith(`${tenantPrefix}/`)
  ) {
    return null;
  }

  // Reject sign-in loop targets so login does not redirect back to itself.
  if (
    normalizedPath === `${tenantPrefix}/sign-in` ||
    normalizedPath.startsWith(`${tenantPrefix}/sign-in/`)
  ) {
    return null;
  }

  // Query string is preserved verbatim. We still cap it to keep storage small
  // and reject control characters that could survive HTML rendering.
  if (rawQuery.length > 2048) {
    return null;
  }
  if (hasAsciiControlCharacters(rawQuery)) {
    return null;
  }

  return rawQuery.length > 0 ? `${normalizedPath}?${rawQuery}` : normalizedPath;
}

function storageKey(tenantCode: string): string {
  return `${STORAGE_PREFIX}${tenantCode}`;
}

function getDefaultStorage(): StorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Persist a return target for the given tenant. The value is validated before
 * write so callers cannot accidentally smuggle an external URL into storage.
 * Returns the normalized target on success, `null` otherwise.
 */
export function saveReturnTarget(
  pathWithQuery: string,
  options: ValidateOptions & {
    source?: ReturnTargetSource;
    storage?: StorageLike | null;
  }
): string | null {
  const normalized = validateReturnTarget(pathWithQuery, options.tenantCode);
  if (normalized === null) {
    return null;
  }
  // H1 LOCK — never persist oidcLand; keep other query params (e.g. x=1).
  const toPersist = stripOidcLandQueryParam(normalized);
  const storage = options.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload: ReturnTargetPayload = {
    pathWithQuery: toPersist,
    createdAtMs: options.nowMs ?? Date.now(),
    ...(options.source !== undefined ? { source: options.source } : {}),
  };
  try {
    storage.setItem(storageKey(options.tenantCode), JSON.stringify(payload));
  } catch {
    return null;
  }
  return toPersist;
}

/**
 * Read (without consuming) the stored return target. Returns the normalized
 * path-with-query if still valid + within TTL; `null` otherwise. Expired /
 * malformed entries are evicted as a side effect to keep storage clean.
 */
export function peekReturnTarget(
  options: ValidateOptions & { storage?: StorageLike | null }
): string | null {
  const storage = options.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const key = storageKey(options.tenantCode);
  const raw = storage.getItem(key);
  if (raw === null) {
    return null;
  }

  let parsed: ReturnTargetPayload | null = null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (
      typeof candidate === 'object' &&
      candidate !== null &&
      typeof (candidate as { pathWithQuery?: unknown }).pathWithQuery ===
        'string' &&
      typeof (candidate as { createdAtMs?: unknown }).createdAtMs === 'number'
    ) {
      parsed = candidate as ReturnTargetPayload;
    }
  } catch {
    parsed = null;
  }
  if (parsed === null) {
    storage.removeItem(key);
    return null;
  }

  const nowMs = options.nowMs ?? Date.now();
  if (nowMs - parsed.createdAtMs > RETURN_TARGET_TTL_MS) {
    storage.removeItem(key);
    return null;
  }

  const normalized = validateReturnTarget(parsed.pathWithQuery, options.tenantCode);
  if (normalized === null) {
    storage.removeItem(key);
    return null;
  }
  // H1 defense-in-depth — strip oidcLand on read so pre-deploy / bypass
  // sessionStorage entries cannot re-enter land after auth (consume uses peek).
  const stripped = stripOidcLandQueryParam(normalized);
  if (stripped !== parsed.pathWithQuery) {
    try {
      storage.setItem(
        key,
        JSON.stringify({ ...parsed, pathWithQuery: stripped })
      );
    } catch {
      // Still return stripped even if heal-write fails.
    }
  }
  return stripped;
}

/**
 * Consume-once: read + remove the stored target. Used by sign-in success and
 * by the post-auth redirect chain.
 */
export function consumeReturnTarget(
  options: ValidateOptions & { storage?: StorageLike | null }
): string | null {
  const storage = options.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const target = peekReturnTarget({ ...options, storage });
  storage.removeItem(storageKey(options.tenantCode));
  return target;
}

export function clearReturnTarget(
  options: { tenantCode: string; storage?: StorageLike | null }
): void {
  const storage = options.storage ?? getDefaultStorage();
  if (storage === null) {
    return;
  }
  storage.removeItem(storageKey(options.tenantCode));
}

/** Convenience: last-known sales-point / tenant shops URL for shop-exit recovery (not fresh login). */
export function buildTenantShopFallback(tenantCode: string): string {
  return resolvePostAuthFallbackPath(tenantCode);
}

export function setInMemoryReturnTarget(pathWithQuery: string | null): void {
  // H1 LOCK — same strip as saveReturnTarget (latent API; zero callers today).
  inMemoryReturnTarget =
    pathWithQuery === null ? null : stripOidcLandQueryParam(pathWithQuery);
}

export function peekInMemoryReturnTarget(): string | null {
  return inMemoryReturnTarget;
}

/** Same-turn consume-once (N11 priority 1). */
export function consumeInMemoryReturnTarget(): string | null {
  const value = inMemoryReturnTarget;
  inMemoryReturnTarget = null;
  return value;
}

/**
 * G2 — publish destination just navigated after consume-once post-auth.
 * Bumps clear generation so an older scheduled clear cannot wipe this publish.
 */
export function publishPostAuthNavigateHandoff(pathWithQuery: string): void {
  postAuthNavigateHandoff = stripOidcLandQueryParam(pathWithQuery);
  postAuthNavigateHandoffClearGeneration += 1;
}

/** Peek SoftRace handoff (does not clear — Strict Mode safe). */
export function peekPostAuthNavigateHandoff(): string | null {
  return postAuthNavigateHandoff;
}

/**
 * Clear SoftRace handoff after signed-in guest-auth Navigate / leave / tests.
 * Call after peek-based resolve so SoftRace restore still wins the bounce.
 * Bumps generation so a pending microtask schedule is a no-op.
 */
export function clearPostAuthNavigateHandoff(): void {
  postAuthNavigateHandoff = null;
  postAuthNavigateHandoffClearGeneration += 1;
}

/**
 * G1 — after SoftRace has had one same-tick peek window, drop the handoff.
 *
 * Contract: call only after `publishPostAuthNavigateHandoff` (or from
 * `navigateToPostAuthReturnTarget`). Synchronous peeks in the same turn — e.g.
 * RequireGuestAuth render before the microtask — still see the handoff.
 * Generation-guarded so a newer publish/clear wins over a stale schedule.
 */
export function scheduleClearPostAuthNavigateHandoffAfterSoftRacePeek(): void {
  const generation = postAuthNavigateHandoffClearGeneration;
  queueMicrotask(() => {
    if (generation !== postAuthNavigateHandoffClearGeneration) {
      return;
    }
    postAuthNavigateHandoff = null;
  });
}

/**
 * G3 SSOT — after SoftRace peek + RequireGuestAuth layout clear-after-win,
 * drop durable + matching per-tenant twin + matching in-memory payment parks
 * so a later accidental `/sign-in` remount cannot sticky-bounce to checkout.
 *
 * Call only after peek-based resolve (not before peek — SoftRace regress).
 * Does **not** run on memory-win consume in {@link resolvePostAuthReturnTarget}
 * (twin/durable stay for SoftRace guest bounce).
 */
export function clearPaymentResumeParksAfterSoftRaceWin(
  tenantCode: string,
  pathWithQuery: string,
  storage?: StorageLike | null,
): void {
  if (!isPaymentResumeReturnPath(pathWithQuery) || !isRealOrgTenantCode(tenantCode)) {
    return;
  }
  clearPaymentResumeReturnTarget(storage);
  const twin = peekReturnTarget({
    tenantCode,
    storage,
  });
  if (twin !== null && twin === pathWithQuery) {
    clearReturnTarget({ tenantCode, storage });
  }
  // Guest bounce peeks memory before durable; leave it and remount sticky-steals.
  const memory = peekInMemoryReturnTarget();
  if (memory !== null && memory === pathWithQuery) {
    setInMemoryReturnTarget(null);
  }
}

/**
 * G1/G2 — after SoftRace peek + RequireGuestAuth layout clear-after-win,
 * drop durable map resume + matching twin + matching in-memory so a later
 * accidental `/sign-in` remount cannot sticky-bounce to the map.
 */
export function clearMapResumeParksAfterSoftRaceWin(
  tenantCode: string,
  pathWithQuery: string,
  storage?: StorageLike | null,
): void {
  if (!isMapResumeReturnPath(pathWithQuery)) {
    return;
  }
  clearMapResumeReturnTarget(storage);
  if (isRealOrgTenantCode(tenantCode)) {
    const twin = peekReturnTarget({
      tenantCode,
      storage,
    });
    if (twin !== null && twin === pathWithQuery) {
      clearReturnTarget({ tenantCode, storage });
    }
  }
  const memory = peekInMemoryReturnTarget();
  if (memory !== null && memory === pathWithQuery) {
    setInMemoryReturnTarget(null);
  }
  // G1/G2/G3 — map SoftRace win must not leave orphan payment twins (incl.
  // PLATFORM_CHROME parks where tenantCode is not a real org).
  clearLeftoverPaymentResumeTwinsAfterMapPark({
    tenantCode,
    storage,
  });
}

function isRealOrgTenantCode(tenantCode: string): boolean {
  const trimmed = tenantCode.trim();
  return (
    trimmed.length > 0 &&
    trimmed !== '__platform__' &&
    !trimmed.startsWith('__') &&
    TENANT_CODE_REGEX.test(trimmed)
  );
}

export function saveOidcPostAuthTenantHint(
  tenantCode: string,
  storage?: StorageLike | null,
): void {
  if (!isRealOrgTenantCode(tenantCode)) {
    return;
  }
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.setItem(OIDC_POST_AUTH_TENANT_HINT_KEY, tenantCode.trim());
  } catch {
    // sessionStorage may be unavailable
  }
}

export function peekOidcPostAuthTenantHint(
  storage?: StorageLike | null,
): string | null {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return null;
  }
  try {
    const raw = store.getItem(OIDC_POST_AUTH_TENANT_HINT_KEY)?.trim() ?? '';
    if (!isRealOrgTenantCode(raw)) {
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

export function clearOidcPostAuthTenantHint(storage?: StorageLike | null): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.removeItem(OIDC_POST_AUTH_TENANT_HINT_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}

function readPaymentResumePayload(
  storage: StorageLike,
  nowMs: number,
): PaymentResumeReturnPayload | null {
  let raw: string | null;
  try {
    raw = storage.getItem(PAYMENT_RESUME_RETURN_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let parsed: PaymentResumeReturnPayload | null = null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (
      typeof candidate === 'object' &&
      candidate !== null &&
      typeof (candidate as { pathWithQuery?: unknown }).pathWithQuery === 'string' &&
      typeof (candidate as { tenantCode?: unknown }).tenantCode === 'string' &&
      typeof (candidate as { createdAtMs?: unknown }).createdAtMs === 'number'
    ) {
      parsed = candidate as PaymentResumeReturnPayload;
    }
  } catch {
    parsed = null;
  }
  if (parsed === null) {
    try {
      storage.removeItem(PAYMENT_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  if (nowMs - parsed.createdAtMs > RETURN_TARGET_TTL_MS) {
    try {
      storage.removeItem(PAYMENT_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  if (!isRealOrgTenantCode(parsed.tenantCode)) {
    try {
      storage.removeItem(PAYMENT_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  const normalized = validateReturnTarget(
    parsed.pathWithQuery,
    parsed.tenantCode,
  );
  if (normalized === null || !isPaymentResumeReturnPath(normalized)) {
    try {
      storage.removeItem(PAYMENT_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  return {
    pathWithQuery: stripOidcLandQueryParam(normalized),
    tenantCode: parsed.tenantCode.trim(),
    createdAtMs: parsed.createdAtMs,
  };
}

/** Peek durable payment-resume park (SoftRace / guest bounce — no consume). */
export function peekPaymentResumeReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readPaymentResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  return payload?.pathWithQuery ?? null;
}

/** Peek tenant code stashed with durable payment-resume (OIDC restore hint). */
export function peekPaymentResumeReturnTenant(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readPaymentResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  return payload?.tenantCode ?? null;
}

export function clearPaymentResumeReturnTarget(
  storage?: StorageLike | null,
): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.removeItem(PAYMENT_RESUME_RETURN_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Consume-once durable payment-resume. Used after in-memory miss so SoftRace /
 * double onSignedIn still return checkout instead of Stage 3 hub.
 */
export function consumePaymentResumeReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readPaymentResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  if (payload === null) {
    return null;
  }
  try {
    storage.removeItem(PAYMENT_RESUME_RETURN_KEY);
  } catch {
    // ignore
  }
  return payload.pathWithQuery;
}

/**
 * Persist durable payment-resume. Returns `false` when storage is unavailable,
 * tenant is not a real org, or `setItem` throws — callers must fail closed.
 */
function writePaymentResumeReturnTarget(
  pathWithQuery: string,
  tenantCode: string,
  options?: { nowMs?: number; storage?: StorageLike | null },
): boolean {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null || !isRealOrgTenantCode(tenantCode)) {
    return false;
  }
  const payload: PaymentResumeReturnPayload = {
    pathWithQuery,
    tenantCode: tenantCode.trim(),
    createdAtMs: options?.nowMs ?? Date.now(),
  };
  try {
    storage.setItem(PAYMENT_RESUME_RETURN_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

function isMapResumeTenantCode(tenantCode: string): boolean {
  const trimmed = tenantCode.trim();
  return trimmed === '__platform__' || isRealOrgTenantCode(trimmed);
}

function isBrowseMapResumeSource(pathWithQuery: string): boolean {
  const withoutHash = pathWithQuery.split('#', 1)[0] ?? pathWithQuery;
  const pathOnly = (withoutHash.split('?', 1)[0] ?? withoutHash).replace(/\/+$/, '') || '/';
  return pathOnly === '/browse' || pathOnly.startsWith('/browse/');
}

function isAcceptedMapResumeNormalizedPath(
  stripped: string,
  normalized: string,
): boolean {
  if (isMapResumeReturnPath(normalized)) {
    return true;
  }
  // Browse kill-switch: stale /browse map parks rewrite to platform hub on consume.
  const normalizedPathOnly = (normalized.split('?')[0] ?? normalized).replace(/\/+$/, '') || '/';
  return isBrowseMapResumeSource(stripped) && isPlatformHubPath(normalizedPathOnly);
}

function readMapResumePayload(
  storage: StorageLike,
  nowMs: number,
): MapResumeReturnPayload | null {
  let raw: string | null;
  try {
    raw = storage.getItem(MAP_RESUME_RETURN_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let parsed: MapResumeReturnPayload | null = null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (
      typeof candidate === 'object' &&
      candidate !== null &&
      typeof (candidate as { pathWithQuery?: unknown }).pathWithQuery === 'string' &&
      typeof (candidate as { tenantCode?: unknown }).tenantCode === 'string' &&
      typeof (candidate as { createdAtMs?: unknown }).createdAtMs === 'number'
    ) {
      parsed = candidate as MapResumeReturnPayload;
    }
  } catch {
    parsed = null;
  }
  if (parsed === null) {
    try {
      storage.removeItem(MAP_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  if (nowMs - parsed.createdAtMs > RETURN_TARGET_TTL_MS) {
    try {
      storage.removeItem(MAP_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  if (!isMapResumeTenantCode(parsed.tenantCode)) {
    try {
      storage.removeItem(MAP_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  const stripped = stripOidcLandQueryParam(parsed.pathWithQuery);
  const tenantOk = isRealOrgTenantCode(parsed.tenantCode)
    ? validateReturnTarget(stripped, parsed.tenantCode)
    : null;
  const platformOk = validatePlatformReturnTarget(stripped);
  const normalized = tenantOk ?? platformOk;
  if (normalized === null || !isAcceptedMapResumeNormalizedPath(stripped, normalized)) {
    try {
      storage.removeItem(MAP_RESUME_RETURN_KEY);
    } catch {
      // ignore
    }
    return null;
  }
  return {
    pathWithQuery: normalized,
    tenantCode: parsed.tenantCode.trim(),
    createdAtMs: parsed.createdAtMs,
  };
}

/** Peek durable map-resume park (SoftRace / guest bounce / OIDC — no consume). */
export function peekMapResumeReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readMapResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  return payload?.pathWithQuery ?? null;
}

/** Peek tenant code stashed with durable map-resume (OIDC restore hint). */
export function peekMapResumeReturnTenant(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readMapResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  return payload?.tenantCode ?? null;
}

export function clearMapResumeReturnTarget(
  storage?: StorageLike | null,
): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.removeItem(MAP_RESUME_RETURN_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Consume-once durable map-resume. Used after in-memory miss so SoftRace /
 * OIDC reload still return the map instead of leftover hub/orders.
 */
export function consumeMapResumeReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload = readMapResumePayload(
    storage,
    options?.nowMs ?? Date.now(),
  );
  if (payload === null) {
    return null;
  }
  try {
    storage.removeItem(MAP_RESUME_RETURN_KEY);
  } catch {
    // ignore
  }
  const normalized = validatePlatformReturnTarget(payload.pathWithQuery);
  return normalized ?? payload.pathWithQuery;
}

/**
 * Persist durable map-resume. Returns `false` when storage is unavailable,
 * tenant is not platform/real-org, or `setItem` throws — callers fail closed.
 */
function writeMapResumeReturnTarget(
  pathWithQuery: string,
  tenantCode: string,
  options?: { nowMs?: number; storage?: StorageLike | null },
): boolean {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null || !isMapResumeTenantCode(tenantCode)) {
    return false;
  }
  const payload: MapResumeReturnPayload = {
    pathWithQuery,
    tenantCode: tenantCode.trim(),
    createdAtMs: options?.nowMs ?? Date.now(),
  };
  try {
    storage.setItem(MAP_RESUME_RETURN_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * G1 — park a map/directory return so it beats leftover payment / orders /
 * platform on the next resolve (in-progress checkout still parks later and
 * clears this slot). All-or-nothing: durable must succeed before competitor
 * clears. Tenant-scoped paths also require a twin; twin failure rolls back
 * durable. `setItem` throw / invalid path → null, competitors intact.
 */
export function parkMapResumeReturnTarget(
  pathWithQuery: string,
  options: ValidateOptions & {
    source?: ReturnTargetSource;
    storage?: StorageLike | null;
  },
): string | null {
  const prepared = withDirectoryMapViewQuery(
    stripOidcLandQueryParam(pathWithQuery),
  );
  if (isAuthLoopReturnPath(prepared) || !isMapResumeReturnPath(prepared)) {
    return null;
  }
  const platformNormalized = validatePlatformReturnTarget(prepared);
  const twinNormalized = isRealOrgTenantCode(options.tenantCode)
    ? validateReturnTarget(prepared, options.tenantCode)
    : null;
  const stored = twinNormalized ?? platformNormalized;
  if (stored === null || !isMapResumeReturnPath(stored)) {
    return null;
  }
  const durableOk = writeMapResumeReturnTarget(stored, options.tenantCode, {
    nowMs: options.nowMs,
    storage: options.storage,
  });
  if (!durableOk) {
    return null;
  }
  if (twinNormalized !== null) {
    const twin = saveReturnTarget(stored, {
      tenantCode: options.tenantCode,
      source: options.source ?? 'shop',
      nowMs: options.nowMs,
      storage: options.storage,
    });
    if (twin === null) {
      clearMapResumeReturnTarget(options.storage);
      return null;
    }
  }
  clearPlatformReturnTarget(options.storage);
  clearPostAuthOrdersIntent(options.storage);
  clearLeftoverPaymentResumeTwinsAfterMapPark({
    tenantCode: options.tenantCode,
    nowMs: options.nowMs,
    storage: options.storage,
  });
  setInMemoryReturnTarget(stored);
  return stored;
}

/**
 * Enumerate real-org tenant codes that still have a per-tenant returnTo row.
 * Skips `customer:returnTo:platform` (hub key) and non-enumerable storages.
 */
function listPerTenantReturnTargetTenantCodes(
  storage: StorageLike | null,
): string[] {
  if (storage === null) {
    return [];
  }
  const length = storage.length;
  const keyAt = storage.key;
  if (typeof length !== 'number' || typeof keyAt !== 'function') {
    return [];
  }
  const tenants: string[] = [];
  try {
    for (let i = 0; i < length; i += 1) {
      const key = keyAt.call(storage, i);
      if (key === null || !key.startsWith(STORAGE_PREFIX)) {
        continue;
      }
      const tenant = key.slice(STORAGE_PREFIX.length).trim();
      // Hub platform key is `customer:returnTo:platform` — not a retail twin.
      if (tenant === 'platform' || !isRealOrgTenantCode(tenant)) {
        continue;
      }
      tenants.push(tenant);
    }
  } catch {
    // Best-effort — private mode / blocked storage must not throw.
  }
  return tenants;
}

/**
 * G1 — leftover checkout twin (`customer:returnTo:{tenant}`) must not outlive
 * a successful map elevate. Peek payment tenant before wiping durable payment;
 * also scan storage so PLATFORM_CHROME / `__platform__` parks still clear
 * orphans when durable payment is already null. Clear only twins that still
 * look like checkout/donate (never a map twin).
 */
function clearLeftoverPaymentResumeTwinsAfterMapPark(options: {
  tenantCode: string;
  nowMs?: number;
  storage?: StorageLike | null;
}): void {
  const storage = options.storage ?? getDefaultStorage();
  const paymentTenant = peekPaymentResumeReturnTenant({
    nowMs: options.nowMs,
    storage,
  });
  const tenants = new Set<string>();
  if (paymentTenant !== null && isRealOrgTenantCode(paymentTenant)) {
    tenants.add(paymentTenant.trim());
  }
  if (isRealOrgTenantCode(options.tenantCode)) {
    tenants.add(options.tenantCode.trim());
  }
  for (const tenant of listPerTenantReturnTargetTenantCodes(storage)) {
    tenants.add(tenant);
  }
  clearPaymentResumeReturnTarget(storage);
  for (const tenant of tenants) {
    const twin = peekReturnTarget({
      tenantCode: tenant,
      nowMs: options.nowMs,
      storage,
    });
    if (twin !== null && isPaymentResumeReturnPath(twin)) {
      clearReturnTarget({ tenantCode: tenant, storage });
    }
  }
}

/**
 * G1 — OIDC `returnTo` for tenantless `platformRedirectToOidcLogin`.
 * Prefer allowlisted parks; bare per-tenant parks stash a hint for FE post-land
 * consume (BE rejects bare `/{tenant}/shop` as platform OIDC returnTo).
 * Never returns `/sign-in` or `/{tenant}/sign-in`.
 *
 * Payment-resume (checkout/donate) beats orders/platform so a leftover hub park
 * cannot divert mid-payment Google/Apple login to `/favorites`.
 */
export function resolveOidcLoginReturnTo(
  tenantCode: string,
  options?: { nowMs?: number; storage?: StorageLike | null },
): string {
  const storage = options?.storage ?? getDefaultStorage();
  const pick = (candidate: string): string => {
    if (isAuthLoopReturnPath(candidate)) {
      return '/account';
    }
    return candidate;
  };

  const paymentResume = peekPaymentResumeReturnTarget({
    nowMs: options?.nowMs,
    storage,
  });
  if (paymentResume !== null) {
    const resumeTenant =
      peekPaymentResumeReturnTenant({
        nowMs: options?.nowMs,
        storage,
      }) ?? (isRealOrgTenantCode(tenantCode) ? tenantCode.trim() : null);
    if (resumeTenant !== null) {
      saveOidcPostAuthTenantHint(resumeTenant, storage);
      return '/account';
    }
  }

  const mapResumeEarly = peekMapResumeReturnTarget({
    nowMs: options?.nowMs,
    storage,
  });
  // G1 — leftover checkout twin must not beat durable map (peek map first).
  if (isRealOrgTenantCode(tenantCode) && mapResumeEarly === null) {
    const perTenantPayment = peekReturnTarget({
      tenantCode: tenantCode.trim(),
      nowMs: options?.nowMs,
      storage,
    });
    if (
      perTenantPayment !== null &&
      isPaymentResumeReturnPath(perTenantPayment)
    ) {
      saveOidcPostAuthTenantHint(tenantCode.trim(), storage);
      return '/account';
    }
  }

  const mapResume = mapResumeEarly;
  if (mapResume !== null) {
    const allowlisted = validatePlatformReturnTarget(mapResume);
    if (allowlisted !== null) {
      clearOidcPostAuthTenantHint(storage);
      return pick(allowlisted);
    }
    const mapTenant =
      peekMapResumeReturnTenant({
        nowMs: options?.nowMs,
        storage,
      }) ?? (isRealOrgTenantCode(tenantCode) ? tenantCode.trim() : null);
    if (mapTenant !== null && isRealOrgTenantCode(mapTenant)) {
      saveOidcPostAuthTenantHint(mapTenant, storage);
      return '/account';
    }
  }

  if (isRealOrgTenantCode(tenantCode)) {
    const perTenantMap = peekReturnTarget({
      tenantCode: tenantCode.trim(),
      nowMs: options?.nowMs,
      storage,
    });
    if (perTenantMap !== null && isMapResumeReturnPath(perTenantMap)) {
      const allowlisted = validatePlatformReturnTarget(perTenantMap);
      if (allowlisted !== null) {
        clearOidcPostAuthTenantHint(storage);
        return pick(allowlisted);
      }
      saveOidcPostAuthTenantHint(tenantCode.trim(), storage);
      return '/account';
    }
  }

  if (peekPostAuthOrdersIntent(storage)) {
    return pick('/account/orders');
  }
  const platform = peekPlatformReturnTarget({
    nowMs: options?.nowMs,
    storage,
  });
  if (platform !== null) {
    clearOidcPostAuthTenantHint(storage);
    return pick(platform);
  }
  if (isRealOrgTenantCode(tenantCode)) {
    const perTenant = peekReturnTarget({
      tenantCode: tenantCode.trim(),
      nowMs: options?.nowMs,
      storage,
    });
    if (perTenant !== null) {
      const allowlisted = validatePlatformReturnTarget(perTenant);
      if (allowlisted !== null) {
        clearOidcPostAuthTenantHint(storage);
        return pick(allowlisted);
      }
      saveOidcPostAuthTenantHint(tenantCode.trim(), storage);
      return '/account';
    }
  }
  clearOidcPostAuthTenantHint(storage);
  return '/account';
}

function isPlatformHubPath(normalizedPath: string): boolean {
  for (const prefix of PLATFORM_HUB_PREFIXES) {
    if (normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)) {
      return true;
    }
  }
  return false;
}

/**
 * G2 — auth-loop parks must never be stored or restored.
 * Covers bare `/sign-in` and `/{tenant}/sign-in` (slug-shaped, otherwise
 * allowlisted by `SALES_POINT_SLUG_REGEX`).
 */
export function isAuthLoopReturnPath(pathWithQuery: string): boolean {
  const pathOnly =
    (pathWithQuery.split('?')[0] ?? pathWithQuery).replace(/\/+$/, '') || '/';
  return (
    pathOnly === '/sign-in' ||
    pathOnly.startsWith('/sign-in/') ||
    /^\/[^/]+\/sign-in(?:\/|$)/.test(pathOnly)
  );
}

/** Safe `reason` values for `/{tenant}/post-kiosk-failure` platform parks. */
const POST_KIOSK_FAILURE_REASON_ALLOWLIST = new Set([
  'EXPIRED',
  'CANCELLED',
  'GATEWAY_ERROR',
  'NETWORK_ERROR',
  'UNKNOWN',
]);

/**
 * Self-Scan resume / deep-link query keys on `/{tenant}/shop/scanner`.
 * Residual G1 — parks accepted only for these keys (plus empty query).
 * When `selfScan=1`, require `kioskId` OR `salesPointId` (digits only).
 */
const SELF_SCAN_SCANNER_QUERY_ALLOWLIST = new Set([
  'kioskId',
  'salesPointId',
  'selfScan',
  'basketPublicId',
]);

/** Basket public-id path/query segment — opaque server id (no `/`, `?`, control chars). */
const SELF_SCAN_BASKET_PUBLIC_ID_REGEX = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * Residual G1 — Self-Scan deep-link / resume path shapes (path-only, no query):
 * - `/{tenant}/shop/scanner`
 * - `/{tenant}/self-scan`
 * - `/{tenant}/self-scan/{basketPublicId}`
 */
export function isSelfScanReturnPath(pathWithQuery: string): boolean {
  const withoutHash = pathWithQuery.split('#', 1)[0] ?? pathWithQuery;
  const pathOnly = (withoutHash.split('?', 1)[0] ?? withoutHash).replace(/\/+$/, '') || '/';
  const segments = pathOnly.split('/').filter((part) => part.length > 0);
  if (segments.length < 2) {
    return false;
  }
  const tenantCode = segments[0] ?? '';
  if (!TENANT_CODE_REGEX.test(tenantCode)) {
    return false;
  }
  if (segments.length === 2) {
    return segments[1] === 'self-scan';
  }
  if (segments.length === 3) {
    const mid = segments[1] ?? '';
    const tail = segments[2] ?? '';
    if (mid === 'shop' && tail === 'scanner') {
      return true;
    }
    return mid === 'self-scan' && SELF_SCAN_BASKET_PUBLIC_ID_REGEX.test(tail);
  }
  return false;
}

/**
 * Self-Scan dedicated route query keys (`/{tenant}/self-scan[/{id}]`).
 * Twin of `buildSelfScanPath` — salesPointId + basketPublicId only (no selfScan/kioskId).
 */
const SELF_SCAN_DEDICATED_QUERY_ALLOWLIST = new Set([
  'salesPointId',
  'basketPublicId',
]);

/**
 * Normalize Self-Scan allowlisted path+query. Returns null when shape/query is not allowlisted.
 * Query on `/shop/scanner`: optional `kioskId`/`salesPointId` (digits), `selfScan=1`, `basketPublicId`.
 * When `selfScan=1`, require `kioskId` OR `salesPointId` (digits only).
 * Query on `/self-scan` / `/self-scan/:id`: optional `salesPointId` (digits), `basketPublicId`
 * (on bare `/self-scan` only — path id wins when present). Reject unknown keys.
 */
function normalizeSelfScanReturnTarget(
  normalizedPath: string,
  rawQuery: string,
): string | null {
  if (!isSelfScanReturnPath(normalizedPath)) {
    return null;
  }
  const segments = normalizedPath.split('/').filter((part) => part.length > 0);
  const tenantCode = segments[0] ?? '';
  const mid = segments[1] ?? '';
  const tail = segments[2];

  if (mid === 'self-scan') {
    const pathBasketId =
      tail !== undefined && SELF_SCAN_BASKET_PUBLIC_ID_REGEX.test(tail) ? tail : null;
    if (tail !== undefined && pathBasketId === null) {
      return null;
    }
    if (rawQuery.length === 0) {
      return pathBasketId !== null
        ? `/${tenantCode}/self-scan/${pathBasketId}`
        : `/${tenantCode}/self-scan`;
    }
    if (hasAsciiControlCharacters(rawQuery) || rawQuery.length > 2048) {
      return null;
    }
    const params = new URLSearchParams(rawQuery);
    const uniqueKeys = [...new Set(params.keys())];
    for (const key of uniqueKeys) {
      if (!SELF_SCAN_DEDICATED_QUERY_ALLOWLIST.has(key)) {
        return null;
      }
    }
    const out = new URLSearchParams();
    const salesPointId = params.get('salesPointId');
    if (salesPointId !== null) {
      if (!/^\d+$/.test(salesPointId)) {
        return null;
      }
      out.set('salesPointId', salesPointId);
    }
    // Path basket id is authoritative; query basketPublicId only on bare /self-scan.
    if (pathBasketId === null) {
      const basketPublicId = params.get('basketPublicId');
      if (basketPublicId !== null) {
        if (!SELF_SCAN_BASKET_PUBLIC_ID_REGEX.test(basketPublicId)) {
          return null;
        }
        out.set('basketPublicId', basketPublicId);
      }
    } else if (params.has('basketPublicId')) {
      const queryBasket = params.get('basketPublicId');
      // Allow matching twin; reject conflicting basket ids.
      if (queryBasket !== null && queryBasket !== pathBasketId) {
        return null;
      }
    }
    const query = out.toString();
    const base =
      pathBasketId !== null
        ? `/${tenantCode}/self-scan/${pathBasketId}`
        : `/${tenantCode}/self-scan`;
    return query.length > 0 ? `${base}?${query}` : base;
  }

  // `/{tenant}/shop/scanner` (+ optional allowlisted query).
  if (mid !== 'shop' || tail !== 'scanner') {
    return null;
  }
  if (rawQuery.length === 0) {
    return `/${tenantCode}/shop/scanner`;
  }
  if (hasAsciiControlCharacters(rawQuery) || rawQuery.length > 2048) {
    return null;
  }
  const params = new URLSearchParams(rawQuery);
  const uniqueKeys = [...new Set(params.keys())];
  for (const key of uniqueKeys) {
    if (!SELF_SCAN_SCANNER_QUERY_ALLOWLIST.has(key)) {
      return null;
    }
  }

  const out = new URLSearchParams();
  const kioskId = params.get('kioskId');
  if (kioskId !== null) {
    if (!/^\d+$/.test(kioskId)) {
      return null;
    }
    out.set('kioskId', kioskId);
  }
  const salesPointId = params.get('salesPointId');
  if (salesPointId !== null) {
    if (!/^\d+$/.test(salesPointId)) {
      return null;
    }
    out.set('salesPointId', salesPointId);
  }
  const selfScan = params.get('selfScan');
  if (selfScan !== null) {
    if (selfScan !== '1') {
      return null;
    }
    // Self-Scan start/resume parks must bind a sales point (kioskId OR salesPointId).
    if (out.get('kioskId') === null && out.get('salesPointId') === null) {
      return null;
    }
    out.set('selfScan', '1');
  }
  const basketPublicId = params.get('basketPublicId');
  if (basketPublicId !== null) {
    if (!SELF_SCAN_BASKET_PUBLIC_ID_REGEX.test(basketPublicId)) {
      return null;
    }
    out.set('basketPublicId', basketPublicId);
  }
  const query = out.toString();
  return query.length > 0
    ? `/${tenantCode}/shop/scanner?${query}`
    : `/${tenantCode}/shop/scanner`;
}

/**
 * Platform return paths (Wave C/D / N11): hubs
 * `/browse|shops|favorites|scan|orgs|account|more|orders/find|help/**`,
 * `/{tenant}/post-kiosk` (+ safe `token` query),
 * `/{tenant}/post-kiosk-failure` (+ required `token`, optional allowlisted `reason`),
 * `/{tenant}/{slug}`, `/{tenant}/shop?kioskId=\d+`,
 * Self-Scan (residual G1 / G9): `/{tenant}/shop/scanner` (+ optional `kioskId`/`salesPointId`/`selfScan=1`/`basketPublicId`;
 * `selfScan=1` requires `kioskId` OR `salesPointId` digits),
 * `/{tenant}/self-scan` (+ optional `salesPointId`/`basketPublicId` query — twin `buildSelfScanPath`),
 * `/{tenant}/self-scan/{basketPublicId}` (+ optional `salesPointId` query).
 * Shape-only — no live tenant fetch.
 * Reject `//`, absolute URLs, `..`, and `/sign-in` / `/{tenant}/sign-in` auth loops.
 * Allowlist semantics match BE `validatePlatformOidcReturnTo` hub prefixes.
 */
export function validatePlatformReturnTarget(rawPathWithQuery: string): string | null {
  if (typeof rawPathWithQuery !== 'string' || rawPathWithQuery.length === 0) {
    return null;
  }
  if (rawPathWithQuery.startsWith('//') || rawPathWithQuery.startsWith('\\')) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(rawPathWithQuery)) {
    return null;
  }

  const [rawPath, ...rawQueryParts] = rawPathWithQuery.split('?');
  const rawQuery = rawQueryParts.join('?');
  const decodedPath = safeRepeatDecode(rawPath ?? '');
  if (decodedPath === null) {
    return null;
  }
  if (decodedPath.startsWith('//') || decodedPath.startsWith('\\')) {
    return null;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(decodedPath)) {
    return null;
  }

  const normalizedPath = normalizePath(decodedPath);
  if (normalizedPath === null) {
    return null;
  }

  // Fail-closed auth loop — never treat /sign-in or /{tenant}/sign-in as parks
  // (`sign-in` matches sales-point slug shape otherwise).
  if (isAuthLoopReturnPath(normalizedPath)) {
    return null;
  }

  if (isPlatformHubPath(normalizedPath)) {
    let effectivePath = normalizedPath;
    let effectiveQuery = rawQuery;
    const flags = resolveBootCustomerFeatureFlags();
    if (
      !isCustomerBrowseEnabled(flags) &&
      (normalizedPath === '/browse' || normalizedPath.startsWith('/browse/'))
    ) {
      effectivePath = resolvePlatformHubPath(flags);
      effectiveQuery = '';
    }
    if (effectiveQuery.length > 0) {
      if (hasAsciiControlCharacters(effectiveQuery) || effectiveQuery.length > 2048) {
        return null;
      }
      return `${effectivePath}?${effectiveQuery}`;
    }
    return effectivePath;
  }

  // Residual G1 — Self-Scan deep-link / resume (2- or 3-segment) before slug / shop.
  const selfScanNormalized = normalizeSelfScanReturnTarget(normalizedPath, rawQuery);
  if (selfScanNormalized !== null) {
    return selfScanNormalized;
  }

  const segments = normalizedPath.split('/').filter((part) => part.length > 0);
  if (segments.length === 2) {
    const tenantCode = segments[0] ?? '';
    const slugOrShop = segments[1] ?? '';
    if (!TENANT_CODE_REGEX.test(tenantCode)) {
      return null;
    }

    // Reserved Self-Scan surface — never treat as sales-point slug when query rejected.
    if (slugOrShop === 'self-scan') {
      return null;
    }

    if (slugOrShop === 'post-kiosk' || slugOrShop === 'post-kiosk-failure') {
      if (rawQuery.length === 0) {
        return `/${tenantCode}/${slugOrShop}`;
      }
      if (hasAsciiControlCharacters(rawQuery) || rawQuery.length > 2048) {
        return null;
      }
      const params = new URLSearchParams(rawQuery);
      const token = params.get('token');
      if (token === null || token.length === 0 || token.length > 512) {
        return null;
      }
      const keys = [...params.keys()];
      if (slugOrShop === 'post-kiosk') {
        // Safe query: only `token` (Wave C post-kiosk).
        if (keys.length !== 1 || keys[0] !== 'token') {
          return null;
        }
        return `/${tenantCode}/post-kiosk?token=${encodeURIComponent(token)}`;
      }
      // post-kiosk-failure: token required; optional reason allowlist.
      const ALLOWED_FAILURE_REASONS = new Set([
        'EXPIRED',
        'CANCELLED',
        'GATEWAY_ERROR',
        'NETWORK_ERROR',
        'UNKNOWN',
      ]);
      for (const key of keys) {
        if (key !== 'token' && key !== 'reason') {
          return null;
        }
      }
      const reasonRaw = params.get('reason');
      const reason =
        reasonRaw !== null && ALLOWED_FAILURE_REASONS.has(reasonRaw)
          ? reasonRaw
          : null;
      if (reason !== null) {
        return `/${tenantCode}/post-kiosk-failure?token=${encodeURIComponent(token)}&reason=${encodeURIComponent(reason)}`;
      }
      return `/${tenantCode}/post-kiosk-failure?token=${encodeURIComponent(token)}`;
    }

    if (slugOrShop === 'post-kiosk-failure') {
      // Token required; optional `reason` from allowlist (unknown reason stripped).
      if (rawQuery.length === 0) {
        return null;
      }
      if (hasAsciiControlCharacters(rawQuery) || rawQuery.length > 2048) {
        return null;
      }
      const params = new URLSearchParams(rawQuery);
      const uniqueKeys = [...new Set(params.keys())];
      const token = params.get('token');
      if (
        token === null ||
        token.length === 0 ||
        token.length > 512 ||
        !uniqueKeys.includes('token') ||
        uniqueKeys.some((key) => key !== 'token' && key !== 'reason')
      ) {
        return null;
      }
      let normalized = `/${tenantCode}/post-kiosk-failure?token=${encodeURIComponent(token)}`;
      const reason = params.get('reason');
      if (
        reason !== null &&
        reason.length > 0 &&
        POST_KIOSK_FAILURE_REASON_ALLOWLIST.has(reason)
      ) {
        normalized += `&reason=${encodeURIComponent(reason)}`;
      }
      return normalized;
    }

    if (slugOrShop === 'shop') {
      if (rawQuery.length === 0) {
        return null;
      }
      if (hasAsciiControlCharacters(rawQuery) || rawQuery.length > 2048) {
        return null;
      }
      const params = new URLSearchParams(rawQuery);
      const keys = [...params.keys()];
      // Safe query: exactly one sales-point id param (`salesPointId` preferred, legacy `kioskId`).
      if (keys.length !== 1) {
        return null;
      }
      const salesPointId = params.get('salesPointId');
      if (
        keys[0] === 'salesPointId' &&
        salesPointId !== null &&
        /^\d+$/.test(salesPointId)
      ) {
        return `/${tenantCode}/shop?salesPointId=${salesPointId}`;
      }
      const kioskId = params.get('kioskId');
      if (keys[0] === 'kioskId' && kioskId !== null && /^\d+$/.test(kioskId)) {
        return `/${tenantCode}/shop?kioskId=${kioskId}`;
      }
      return null;
    }

    const slug = normalizeSalesPointSlug(slugOrShop);
    if (slug === null) {
      return null;
    }
    if (rawQuery.length > 0) {
      return null;
    }
    return `/${tenantCode}/${slug}`;
  }

  return null;
}

export function savePlatformReturnTarget(
  pathWithQuery: string,
  options?: { nowMs?: number; storage?: StorageLike | null }
): string | null {
  const normalized = validatePlatformReturnTarget(pathWithQuery);
  if (normalized === null) {
    return null;
  }
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const payload: ReturnTargetPayload = {
    pathWithQuery: normalized,
    createdAtMs: options?.nowMs ?? Date.now(),
  };
  try {
    storage.setItem(PLATFORM_RETURN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return null;
  }
  return normalized;
}

export function peekPlatformReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const raw = storage.getItem(PLATFORM_RETURN_STORAGE_KEY);
  if (raw === null) {
    return null;
  }

  let parsed: ReturnTargetPayload | null = null;
  try {
    const candidate = JSON.parse(raw) as unknown;
    if (
      typeof candidate === 'object' &&
      candidate !== null &&
      typeof (candidate as { pathWithQuery?: unknown }).pathWithQuery === 'string' &&
      typeof (candidate as { createdAtMs?: unknown }).createdAtMs === 'number'
    ) {
      parsed = candidate as ReturnTargetPayload;
    }
  } catch {
    parsed = null;
  }
  if (parsed === null) {
    storage.removeItem(PLATFORM_RETURN_STORAGE_KEY);
    return null;
  }

  const nowMs = options?.nowMs ?? Date.now();
  if (nowMs - parsed.createdAtMs > RETURN_TARGET_TTL_MS) {
    storage.removeItem(PLATFORM_RETURN_STORAGE_KEY);
    return null;
  }

  const normalized = validatePlatformReturnTarget(parsed.pathWithQuery);
  if (normalized === null) {
    storage.removeItem(PLATFORM_RETURN_STORAGE_KEY);
    return null;
  }
  return normalized;
}

/** Platform consume-once (N11 priority 2). */
export function consumePlatformReturnTarget(options?: {
  nowMs?: number;
  storage?: StorageLike | null;
}): string | null {
  const storage = options?.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const target = peekPlatformReturnTarget({ ...options, storage });
  storage.removeItem(PLATFORM_RETURN_STORAGE_KEY);
  return target;
}

/** Persist platform-guest intent to open account orders after sign-in. */
export function savePostAuthOrdersIntent(storage?: StorageLike | null): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.setItem(POST_AUTH_ORDERS_INTENT_KEY, '1');
  } catch {
    // sessionStorage may be unavailable — post-auth falls through to shop.
  }
}

/** Read (without consuming) platform orders post-auth intent. */
export function peekPostAuthOrdersIntent(storage?: StorageLike | null): boolean {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return false;
  }
  try {
    return store.getItem(POST_AUTH_ORDERS_INTENT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Consume-once platform orders intent. */
export function consumePostAuthOrdersIntent(storage?: StorageLike | null): boolean {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return false;
  }
  try {
    const raw = store.getItem(POST_AUTH_ORDERS_INTENT_KEY);
    store.removeItem(POST_AUTH_ORDERS_INTENT_KEY);
    return raw === '1';
  } catch {
    return false;
  }
}

/** Drop stale platform orders intent without consuming post-auth navigation. */
export function clearPostAuthOrdersIntent(storage?: StorageLike | null): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.removeItem(POST_AUTH_ORDERS_INTENT_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}

/** Drop stale platform return park without consuming post-auth navigation. */
export function clearPlatformReturnTarget(storage?: StorageLike | null): void {
  const store = storage ?? getDefaultStorage();
  if (store === null) {
    return;
  }
  try {
    store.removeItem(PLATFORM_RETURN_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * G1 — payment-resume path predicate SSOT (sibling module).
 * Journey `/{tenant}/donate` + `/{tenant}/checkout(/complete)?` elevate;
 * `/account/**` and `/{tenant}/account/**` (incl. donations) do not.
 */
export {
  inferPaymentResumeReturnSource,
  isPaymentResumeReturnPath,
} from './isPaymentResumeReturnPath.js';

export { isMapResumeReturnPath } from './isMapResumeReturnPath.js';

/** @deprecated Prefer {@link inferPaymentResumeReturnSource}. */
export { inferPaymentResumeReturnSource as inferPaymentResumeSource } from './isPaymentResumeReturnPath.js';

/**
 * G1 — park a payment-resume return target so it beats leftover platform /
 * orders competitors on the next `resolvePostAuthReturnTarget`. Elevate is
 * all-or-nothing: twin + durable must both succeed before clearing
 * competitors / publishing in-memory. Durable failure rolls back twin +
 * memory and returns null so CTAs null-guard refuse navigate; platform /
 * orders competitors stay intact.
 */
export function parkPaymentResumeReturnTarget(
  pathWithQuery: string,
  options: ValidateOptions & {
    source?: ReturnTargetSource;
    storage?: StorageLike | null;
  },
): string | null {
  const stored = saveReturnTarget(pathWithQuery, options);
  if (stored === null) {
    return null;
  }
  if (!isPaymentResumeReturnPath(stored)) {
    clearReturnTarget({
      tenantCode: options.tenantCode,
      storage: options.storage,
    });
    return null;
  }
  // Durable before competitor clears — fail closed without wiping platform/orders.
  const durableOk = writePaymentResumeReturnTarget(
    stored,
    options.tenantCode,
    {
      nowMs: options.nowMs,
      storage: options.storage,
    },
  );
  if (!durableOk) {
    clearReturnTarget({
      tenantCode: options.tenantCode,
      storage: options.storage,
    });
    setInMemoryReturnTarget(null);
    return null;
  }
  clearPlatformReturnTarget(options.storage);
  clearPostAuthOrdersIntent(options.storage);
  clearMapResumeReturnTarget(options.storage);
  setInMemoryReturnTarget(stored);
  return stored;
}

/**
 * Wave D — rewrite legacy `/{tenant}/account…` parks to platform chrome
 * before navigate/emit. Shared by post-auth consume and guest bounce peek.
 * G2 — never rewrite `/{tenant}/sign-in` → `/sign-in` (auth-loop); leave path
 * unchanged so callers that skip auth-loop parks fail closed.
 */
export function toPlatformChromeEmitPath(path: string): string {
  if (isAuthLoopReturnPath(path)) {
    return path;
  }
  const accountMatch = path.match(/^\/[^/]+\/(account(?:\/.*)?)$/);
  if (accountMatch?.[1] !== undefined) {
    return `/${accountMatch[1]}`;
  }
  return path;
}

function emitSafePostAuthPath(path: string): string | null {
  const emitted = toPlatformChromeEmitPath(path);
  if (isAuthLoopReturnPath(emitted) || isAuthLoopReturnPath(path)) {
    return null;
  }
  return emitted;
}

/**
 * N11 / Wave C post-auth return priority:
 * in-memory → durable payment-resume → durable map-resume → orders intent
 * (`/account/orders`) → platform consume-once → per-tenant → favs when ≥1
 * oblíbené, else `/browse`.
 * Bare `/{tenant}/shop` is not a fresh-login landing.
 * Wave D / G5 — allowlisted legacy `/{tenant}/account/**` emits bare `/account/**`
 * (same spirit as guest bounce `toPlatformChromeEmitPath`).
 *
 * SoftRace: do **not** clear the per-tenant twin when in-memory wins — after the
 * handoff microtask clears, RequireGuestAuth must still peek twin/durable and
 * land checkout/map (not Stage 3 hub with an empty cart).
 */
export function resolvePostAuthReturnTarget(
  tenantCode: string,
  options?: { nowMs?: number; storage?: StorageLike | null }
): string {
  const memory = consumeInMemoryReturnTarget();
  if (memory !== null) {
    const emitted = emitSafePostAuthPath(memory);
    if (emitted !== null) {
      return emitted;
    }
  }
  const paymentResume = consumePaymentResumeReturnTarget(options);
  if (paymentResume !== null) {
    // Keep per-tenant twin in sync when durable wins (consume-once pair).
    if (isRealOrgTenantCode(tenantCode)) {
      const twin = peekReturnTarget({
        tenantCode,
        nowMs: options?.nowMs,
        storage: options?.storage,
      });
      if (twin !== null && twin === paymentResume) {
        clearReturnTarget({ tenantCode, storage: options?.storage });
      }
    }
    const emitted = emitSafePostAuthPath(paymentResume);
    if (emitted !== null) {
      return emitted;
    }
  }
  const mapResume = consumeMapResumeReturnTarget(options);
  if (mapResume !== null) {
    if (isRealOrgTenantCode(tenantCode)) {
      const twin = peekReturnTarget({
        tenantCode,
        nowMs: options?.nowMs,
        storage: options?.storage,
      });
      if (twin !== null && twin === mapResume) {
        clearReturnTarget({ tenantCode, storage: options?.storage });
      }
    }
    const emitted = emitSafePostAuthPath(mapResume);
    if (emitted !== null) {
      return emitted;
    }
  }
  if (consumePostAuthOrdersIntent(options?.storage)) {
    return '/account/orders';
  }
  const platform = consumePlatformReturnTarget(options);
  if (platform !== null) {
    const emitted = emitSafePostAuthPath(platform);
    if (emitted !== null) {
      return emitted;
    }
  }
  // Platform chrome / missing home slug: do not invent a tenant-scoped park.
  if (isRealOrgTenantCode(tenantCode)) {
    const perTenant = consumeReturnTarget({
      tenantCode,
      nowMs: options?.nowMs,
      storage: options?.storage,
    });
    if (perTenant !== null) {
      const emitted = emitSafePostAuthPath(perTenant);
      if (emitted !== null) {
        return emitted;
      }
    }
  }
  return resolveDefaultPostAuthLandingPath(tenantCode, options?.nowMs);
}

/**
 * Navigate after password/OTP (and post-OIDC FE restore): consume parks then
 * publish G2 handoff so guest-auth peek cannot overwrite with default hub.
 *
 * G1 lifetime: publish → navigate → schedule microtask clear. Same-tick SoftRace
 * peek (RequireGuestAuth) still wins; after settle, handoff is gone so a later
 * guest-auth visit cannot steal. Product freeze: no `?tenant=` login bind.
 *
 * SoftRace harden: if a prior same-turn navigate already published a payment
 * handoff, reuse it instead of re-resolving to Stage 3 hub after parks were
 * consumed (double onSignedIn / Strict Mode).
 */
export function navigateToPostAuthReturnTarget(
  tenantCode: string,
  navigate: (to: string, opts?: { replace?: boolean }) => void,
  options?: { nowMs?: number; storage?: StorageLike | null; replace?: boolean },
): string {
  const existingHandoff = peekPostAuthNavigateHandoff();
  const target =
    existingHandoff !== null &&
    (isPaymentResumeReturnPath(existingHandoff) ||
      isMapResumeReturnPath(existingHandoff))
      ? existingHandoff
      : resolvePostAuthReturnTarget(tenantCode, options);
  publishPostAuthNavigateHandoff(target);
  if (options?.replace === true) {
    navigate(target, { replace: true });
  } else {
    navigate(target);
  }
  scheduleClearPostAuthNavigateHandoffAfterSoftRacePeek();
  // G3 — do NOT clear durable/twin payment or map parks here. SoftRace guest
  // peek must still read them after handoff microtask; RequireGuestAuth
  // clear-after-win owns the wipe.
  return target;
}
